import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button, Icon, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  FinanceJournalLineFilter,
  ProjectionKind,
  useReconciliationBankJournalLinesDataTable,
} from 'generated/resources/datatable.graphql'
import { sum } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useEffect } from 'modules/core/hooks/useEffect'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/finance/components/JournalsEntry/Datatable/_Amount'
import { EmptyBlock } from 'modules/finance/components/ReconciliationSidepanel/EmptyBlock'

interface Props {
  onSelect: (journalLineId: number | null) => void
  onSnooze: (journalLineId: number, snoozeTime: Date) => void
  onUnsnooze: (journalLineId: number) => void
  selectedJournalLineId?: number | null
  bankAccountId: number
  currency: string
}

const TimeUnits = {
  DAY: 24 * 3600 * 1000,
  WEEK: 7 * 24 * 3600 * 1000,
  MONTH: 31 * 24 * 3600 * 1000,
  FOREVER: 200 * 12 * 31 * 24 * 3600 * 1000,
}

export const ReconciliationBankJournalLinesDataTable = ({
  onSelect,
  onSnooze,
  onUnsnooze,
  bankAccountId,
  selectedJournalLineId,
  currency,
}: Props) => {
  const [filter, setFilter] = useState('')

  const onRowSelect = (transactionId: number) => {
    onSelect(transactionId)
  }
  const apolloClient = useApolloClient()

  useEffect(() => {
    if (!selectedJournalLineId) {
      apolloClient.refetchQueries({
        include: [source.query],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJournalLineId])

  const snoozeFor = (journalLineId: number, time: number) => () => {
    onSnooze(journalLineId, new Date(Date.now() + time))
  }

  const filters: DataTableFilter<FinanceJournalLineFilter>[] = [
    {
      identifier: 'reconciledStatusFilter',
      title: i18n.t('finance.reconciliations.datatable.filters.reconciliation_type.title'),
      path: 'reconciledStatus',
      default: true,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          value: ['pending'],
          hideSearch: true,
          operation: 'in',
          options: [
            {
              label: i18n.t(
                'finance.reconciliations.datatable.filters.reconciliation_type.options.pending'
              ),
              value: 'pending',
            },
            {
              label: i18n.t(
                'finance.reconciliations.datatable.filters.reconciliation_type.options.reconciled'
              ),
              value: 'reconciled',
            },
            {
              label: i18n.t(
                'finance.reconciliations.datatable.filters.reconciliation_type.options.snoozed'
              ),
              value: 'snoozed',
            },
          ],
        },
      ],
    },
    {
      identifier: 'publishedAt',
      title: i18n.t('finance.ledger_account_detail.datatable.fields.recorded_at'),
      path: 'publishedAt',
      default: false,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
        },
      ],
    },
  ]

  const source = useReconciliationBankJournalLinesDataTable(
    [
      {
        identifier: 'publishedAt',
        title: i18n.t('finance.ledger_account_detail.datatable.fields.recorded_at'),
        kind: ProjectionKind.Custom,
        width: 130,
        render: ({ id, journalEntry_publishedAt }) => (
          <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
            <Text.Normal size="200" color={isSelectedJournalLine(id) ? 'viridian1000' : undefined}>
              {dateBackendDateTimeToUI(String(journalEntry_publishedAt))}
            </Text.Normal>
          </Box>
        ),
        template: '{{ publishedAt }}',
      },
      {
        identifier: 'journalEntry_description',
        title: i18n.t(
          'finance.ledger_account_detail.datatable.fields.journal_entry_description_v2'
        ),
        kind: ProjectionKind.Custom,
        render: ({ id, journalEntry_description }) => (
          <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
            <Text.Normal size="200" color={isSelectedJournalLine(id) ? 'viridian1000' : undefined}>
              {journalEntry_description}
            </Text.Normal>
          </Box>
        ),
        template: '{{ journalEntry_description }}',
      },
      {
        identifier: 'debit',
        width: 120,
        title: i18n.t('finance.ledger_account_detail.datatable.fields.debit'),
        kind: ProjectionKind.Custom,
        render: ({ id, debitAmountCents }) => (
          <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
            <Amount
              amountCents={debitAmountCents}
              currency={currency}
              color={isSelectedJournalLine(id) ? 'viridian1000' : undefined}
            />
          </Box>
        ),
        template: '{{ - }}',
      },
      {
        identifier: 'credit',
        width: 120,
        title: i18n.t('finance.ledger_account_detail.datatable.fields.credit'),
        kind: ProjectionKind.Custom,
        render: ({ id, creditAmountCents }) => (
          <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
            <Amount
              amountCents={creditAmountCents}
              currency={currency}
              color={isSelectedJournalLine(id) ? 'viridian1000' : undefined}
            />
          </Box>
        ),
        template: '{{ - }}',
      },
      {
        identifier: 'percentage',
        width: 80,
        kind: ProjectionKind.Decorative,
        render: (journalLine) => {
          const totalReconciledAmountCents = sum(
            journalLine.reconciliations.map((r) => r.journalLineReconciledAmountCents)
          )
          const colorBar = isSelectedJournalLine(journalLine.id) ? 'viridian200' : 'grey400'
          const colorProgressBar = 'viridian800'
          return (
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceBetween"
              style={{ cursor: 'pointer', maxWidth: '180px' }}
              onClick={() => onRowSelect(journalLine.id)}
            >
              <Box minWidth="s48">
                <Box width="full">
                  <ProgressBar
                    barColor={colorBar}
                    progressColor={colorProgressBar}
                    size="medium"
                    amount={Math.abs(totalReconciledAmountCents)}
                    total={
                      Math.abs(journalLine.creditAmountCents) +
                      Math.abs(journalLine.debitAmountCents)
                    }
                  />
                </Box>
              </Box>
            </Box>
          )
        },
      },
      {
        identifier: 'snoozes',
        kind: ProjectionKind.Decorative,
        width: 32,
        render: ({ id, snoozedUntil }) => {
          return (
            <Box
              style={{
                height: '20px',
                marginTop: '-10px',
                marginLeft: '-20px',
              }}
            >
              <PopoverMenu type="list">
                <PopoverMenu.Trigger asChild>
                  <Button iconName="ellipsis" type="secondary" />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="end">
                  {snoozedUntil === null ? (
                    <>
                      <PopoverMenu.TouchableOption
                        title={i18n.t('finance.reconciliations.datatable.actions.snooze_for_a_day')}
                        onClick={snoozeFor(id, TimeUnits.DAY)}
                      />
                      <PopoverMenu.TouchableOption
                        title={i18n.t(
                          'finance.reconciliations.datatable.actions.snooze_for_a_week'
                        )}
                        onClick={snoozeFor(id, TimeUnits.WEEK)}
                      />
                      <PopoverMenu.TouchableOption
                        title={i18n.t(
                          'finance.reconciliations.datatable.actions.snooze_for_a_month'
                        )}
                        onClick={snoozeFor(id, TimeUnits.MONTH)}
                      />
                      <PopoverMenu.TouchableOption
                        title={i18n.t('finance.reconciliations.datatable.actions.snooze_forever')}
                        onClick={snoozeFor(id, TimeUnits.FOREVER)}
                      />
                    </>
                  ) : (
                    <PopoverMenu.TouchableOption
                      title={i18n.t('finance.reconciliations.datatable.actions.unsnooze')}
                      onClick={() => onUnsnooze(id)}
                    />
                  )}
                </PopoverMenu.Content>
              </PopoverMenu>
            </Box>
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 32,
        render: ({ id }) => {
          return (
            <Box
              flexDirection="row"
              style={{
                cursor: 'pointer',
                width: '24px',
                height: '20px',
                marginTop: '-2px',
                marginLeft: '-20px',
              }}
              onClick={() => onRowSelect(id)}
            >
              <Icon.Medium
                icon="arrow-right"
                color={isSelectedJournalLine(id) ? 'viridian1000' : undefined}
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        bankAccountIds: [bankAccountId],
        description: filter,
      },
    }
  )

  const isSelectedJournalLine = (id: number) => id === selectedJournalLineId

  return (
    <Box minWidth="s600" overflowY="auto">
      <DataTable.Provider
        source={source}
        sort={{ path: 'id', direction: 'desc' }}
        filters={filters}
        enableClearFilters
        pagination={{
          kind: 'offset',
        }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableDefaultActions
            search={{
              value: filter,
              onChange: (ev) => setFilter(ev.target.value),
              placeholder: i18n.t('finance.reconciliations.datatable.filters.description.title'),
            }}
          />
          <DataTable.Table emptyState={<EmptyBlock />} />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}
